// Core
import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
// Services
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';
import { ReplaySubject } from 'rxjs';

import { IProfessionalProfile } from '../../interfaces/professionalprofile.interface';
// Types
import {
	TitleData,
	TodoItem,
} from '../../interfaces/profile-completion.interface';
import { UserService } from '../../services/user.service';

@Component({
	selector: 'app-profile-completion',
	templateUrl: './profile-completion.component.html',
	styleUrls: ['./profile-completion.component.scss'],
	encapsulation: ViewEncapsulation.None,
})
export class ProfileCompletionComponent implements OnInit {
	public hide = true;
	public hideSection = true;
	public data: TitleData = [];
	public isLoading = false;
	public isUserActive: boolean;
	public requestCompletionStatusError: string = null;
	public appearOnSearchModalOpen = false;
	@Input() reset: ReplaySubject<boolean> = new ReplaySubject<boolean>();
	@Input() hideDetails: ReplaySubject<boolean> = new ReplaySubject<boolean>();

	constructor(
		private translate: TranslateService,
		private userService: UserService,
		private notificationService: ZkNotificationService
	) {}

	ngOnInit(): void {
		this.hideDetails.asObservable().subscribe((iHideDetails) => {
			this.hideSection = iHideDetails;
		});

		this.reset.asObservable().subscribe(async (reset) => {
			if (reset) {
				this.isLoading = true;

				this.userService
					.getProfessionalProfileData(await this.userService.getUserId())
					.subscribe((profile: IProfessionalProfile) => {
						if (profile) {
							this.isUserActive = profile.active;
							this.userService.requestCompletionStatus().subscribe(
								(response) => {
									const steps = response.steps;
									const activateProfile = steps.activateProfile
										? this.getIndicator(steps.activateProfile.todo)
										: this.getIndicator([
												{ completed: true, text: 'Activado' },
										  ]);
									const performSessions = this.getIndicator(
										steps.performSessions.todo
									);
									const issueReceipts = this.getIndicator(
										steps.issueReceipts.todo
									);

									this.isLoading = false;

									const activateProfileDone = steps.activateProfile
										? steps.activateProfile.todo.filter((it) => !it.completed)
												.length === 0
										: true;

									const performSessionsDone =
										steps.performSessions.todo.filter((it) => !it.completed)
											.length === 0;

									const issueReceiptsDone =
										steps.issueReceipts.todo.filter((it) => !it.completed)
											.length === 0;

									const allDone =
										activateProfileDone &&
										performSessionsDone &&
										issueReceiptsDone;

									// Hide steps when the user fills everything
									if (!allDone) {
										this.hide = false;
										this.hideSection = false;
									}

									const viewedSearchModal = JSON.parse(
										localStorage.getItem('viewedAppearOnSearchModal')
									);

									if (profile?.profileCompletion?.completedActivateProfileAt) {
										const now = new Date().getTime();
										const appearOnSearchAt = new Date(
											profile.profileCompletion.completedActivateProfileAt
										).getTime();

										// Show "appearOnSearchModal" only if user filled data in the last 10 minutes
										if (
											activateProfileDone &&
											now - appearOnSearchAt <= 60000 * 10 &&
											!viewedSearchModal
										) {
											this.appearOnSearchModalOpen = true;
										}
									}

									this.data = [
										{
											type: 'performSessions',
											title: this.translate.instant(
												'PAGES.ACCOUNT.PROFILE__COMPLETION.PERFORM__SESSIONS.TITLE',
												{ indicator: performSessions, index: 2 }
											),
											payload: steps.performSessions.todo,
										},
										{
											type: 'issueReceipts',
											title: this.translate.instant(
												'PAGES.ACCOUNT.PROFILE__COMPLETION.ISSUE__RECEIPTS.TITLE',
												{ indicator: issueReceipts, index: 3 }
											),
											payload: steps.issueReceipts.todo,
										},
										{
											type: 'activateProfile',
											title: this.translate.instant(
												'PAGES.ACCOUNT.PROFILE__COMPLETION.ACTIVATE__PROFILE.TITLE',
												{ indicator: activateProfile, index: 1 }
											),
											payload: steps.activateProfile
												? steps.activateProfile.todo
												: [{ completed: true, text: 'Activado' }],
										},
									];
								},
								(error) => {
									this.isLoading = false;
									this.requestCompletionStatusError = this.translate.instant(
										'ERRORS.CONFERENCE.ERROROCCURRED'
									);
									this.notificationService.showError(error.message);
								}
							);
						}
					});
			}
		});
	}

	private getAllCompletedValues(array: readonly TodoItem[]) {
		return array.map((data) => data.completed);
	}

	private getOnlyTrueValues(array: readonly boolean[]) {
		return array.filter((boolean) => boolean).length;
	}

	private getPercentage(array: readonly TodoItem[]) {
		const allCompletedValues = this.getAllCompletedValues(array);
		const numberTrueValues = this.getOnlyTrueValues(allCompletedValues);
		const percentage = (numberTrueValues / array.length) * 100;
		return Math.round(percentage);
	}

	private getClosestNumber(num: number, arr: number[]): number {
		let curr = arr[0];
		let diff = Math.abs(num - curr);
		for (let val = 0; val < arr.length; val++) {
			const newdiff = Math.abs(num - arr[val]);
			if (newdiff < diff) {
				diff = newdiff;
				curr = arr[val];
			}
		}
		return curr;
	}

	private getIndicator(array: readonly TodoItem[]): string {
		const percentage = this.getPercentage(array) ?? 0;

		const snaps: number[] = [0, 15, 30, 45, 50, 60, 75, 90, 100];
		const displayPercentage = this.getClosestNumber(percentage, snaps);

		const innerIndicator =
			percentage === 100
				? `
						<span class="label">
							<img
								class="profile-completion--check-icon"
								src="/assets/images/icons/check.svg"
							/>
						</span>
					`
				: `<span class="label">${percentage}%</span>`;

		return `<div class="set-size charts-container">
			<div class="pie-wrapper progress-${displayPercentage}">
				${innerIndicator}
				<div class="pie">
					<div class="left-side half-circle"></div>
					<div class="right-side half-circle"></div>
				</div>
				<div class="pie-background-circle"></div>
			</div>
		</div>`;
	}

	closeAppearOnSearchModal(): void {
		this.appearOnSearchModalOpen = false;
		localStorage.setItem('viewedAppearOnSearchModal', 'true');
	}
}
